/**
 * The only door.
 *
 * Per ADR-0016 membership of the `dsa-labs.com` domain is membership of the
 * Workspace, so this screen offers Google and nothing else — no registration
 * link, because there is no registration. The password form appears only where
 * a deployment has turned the break-glass route on, and is presented as what it
 * is rather than as a peer of the Google button.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, api } from "../api";
import { useAuth } from "../auth";
import { AuthLayout } from "../components/Layout";
import { GoogleSignInButton } from "../components/GoogleSignInButton";
import { Banner, Button, Field, Spinner, TextInput } from "../components/ui";

export function SignIn() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: methods, isPending } = useQuery({
    queryKey: ["sign-in-methods"],
    queryFn: api.signInMethods,
    // Which doors are open is deployment configuration, not session state.
    staleTime: Infinity,
  });

  async function signInWithGoogle(credential: string) {
    setBusy(true);
    setError(null);
    try {
      setUser(await api.googleSignIn(credential));
      navigate("/", { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Sign in failed. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setUser(await api.login({ email, password }));
      navigate("/", { replace: true });
    } catch (caught) {
      // One message covers both a wrong email and a wrong password, because the
      // server deliberately does not say which.
      setError(caught instanceof ApiError ? caught.message : "Sign in failed.");
    } finally {
      setBusy(false);
    }
  }

  if (isPending) return <Spinner label="Loading sign-in" />;

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Player ranking sources, runs and output."
      footer={
        methods?.hosted_domain ? (
          <>Use your {methods.hosted_domain} account. Access is limited to that domain.</>
        ) : null
      }
    >
      <div className="flex flex-col gap-md">
        {error && <Banner tone="danger" title={error} />}

        {methods?.google ? (
          <GoogleSignInButton
            clientId={methods.google_client_id}
            hostedDomain={methods.hosted_domain}
            onCredential={signInWithGoogle}
            onError={setError}
          />
        ) : (
          <Banner tone="warning" title="Google sign-in is not configured">
            {/*
              Naming the variable is the difference between someone fixing this
              in a minute and filing a bug about a broken portal.
            */}
            Set PORTAL_GOOGLE_CLIENT_ID on the portal and restart it.
          </Banner>
        )}

        {methods?.password && (
          <form onSubmit={submitPassword}>
            <div className="flex flex-col gap-md">
              <p className="text-body-sm text-muted text-center">
                Or sign in with a password.
              </p>
              <Field label="Email">
                <TextInput
                  type="email"
                  value={email}
                  autoComplete="username"
                  required
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
              <Field label="Password">
                <TextInput
                  type="password"
                  value={password}
                  autoComplete="current-password"
                  required
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Field>
              <Button type="submit" variant="secondary" disabled={busy} className="w-full">
                {busy ? "Signing in…" : "Sign in with a password"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
